/* global prepareSupportMessageRequest, authorizedPost, runtime, t */
(function (global) {
  let supportCapabilitiesPromise = null;
  const SUPPORT_AUTH_TIMEOUT_MS = 15000;
  const SUPPORT_CAPABILITIES_TIMEOUT_MS = 15000;
  const SUPPORT_REQUEST_TIMEOUT_MS = 40000;
  const requireSupportBusiness = () => {
    const businessId = String(runtime.activeBusinessId || '').trim();
    if (!businessId) throw new Error(t('support.error.noBusiness'));
    return businessId;
  };

  const supportRequest = async (action, payload = {}) => authorizedPost(
    'customerSupportTicketsHttp',
    {
      businessId: requireSupportBusiness(),
      action,
      ...payload,
    },
    {
      authTimeoutMs: SUPPORT_AUTH_TIMEOUT_MS,
      requestTimeoutMs: action === 'capabilities' ? SUPPORT_CAPABILITIES_TIMEOUT_MS : SUPPORT_REQUEST_TIMEOUT_MS,
      timeoutMessage: t('support.error.requestTimedOut'),
    },
  );

  const requireSupportTicket = (ticket) => {
    if (!ticket || !String(ticket.id || '').trim()) throw new Error('Invalid support ticket response.');
    return ticket;
  };

  const getSupportCapabilities = async () => {
    if (!supportCapabilitiesPromise) {
      const pending = supportRequest('capabilities').then((response) => response?.capabilities || {});
      supportCapabilitiesPromise = pending;
      pending.catch(() => {
        if (supportCapabilitiesPromise === pending) supportCapabilitiesPromise = null;
      });
    }
    try {
      return await supportCapabilitiesPromise;
    } catch (_) {
      return {};
    }
  };

  const warmSupportService = async () => {
    await getSupportCapabilities();
  };

  const prepareMessage = async (message) => {
    const draft = typeof message === 'string' ? { body: message, images: [] } : (message || { body: '', images: [] });
    if (draft.images?.length) {
      const capabilities = await getSupportCapabilities();
      if (capabilities.imageAttachments !== true) throw new Error(t('support.error.imagesUnavailable'));
    }
    return prepareSupportMessageRequest(draft);
  };

  const listSupportTickets = async () => {
    const response = await supportRequest('list');
    return Array.isArray(response?.tickets) ? response.tickets : [];
  };

  const getSupportTicketThread = async (ticketId) => {
    const response = await supportRequest('thread', { ticketId });
    const ticket = response?.ticket || null;
    // Reading the thread marks it read server-side (customerUnread: false), so
    // refresh the nav badge now instead of waiting for the next focus/poll.
    if (ticket) window.dispatchEvent(new CustomEvent('plumme-support-tickets-changed', { detail: ticket }));
    return {
      ticket,
      messages: Array.isArray(response?.messages) ? response.messages : [],
    };
  };

  const createSupportTicket = async ({ category, subject, message, body, sourceRoute } = {}) => {
    const content = await prepareMessage(message || body || '');
    const response = await supportRequest('create', { category, subject, ...content, sourceRoute });
    const ticket = requireSupportTicket(response?.ticket);
    if ((content.attachments?.length || 0) !== (response?.message?.attachments?.length || 0)) {
      throw new Error(t('support.error.imagesUnavailable'));
    }
    const createdMessage = response?.message && String(response.message.id || '').trim() ? response.message : null;
    window.dispatchEvent(new CustomEvent('plumme-support-tickets-changed', { detail: ticket }));
    return { ticket, message: createdMessage };
  };

  const replyToSupportTicket = async (ticketId, message) => {
    const content = await prepareMessage(message);
    const response = await supportRequest('reply', { ticketId, ...content });
    const ticket = requireSupportTicket(response?.ticket);
    if (!response?.message || !String(response.message.id || '').trim()) throw new Error('Invalid support message response.');
    if ((content.attachments?.length || 0) !== (response.message.attachments?.length || 0)) {
      throw new Error(t('support.error.imagesUnavailable'));
    }
    window.dispatchEvent(new CustomEvent('plumme-support-tickets-changed', { detail: ticket }));
    return { ...response, ticket };
  };

  const setSupportTicketStatus = async (ticketId, status) => {
    const response = await supportRequest('status', { ticketId, status });
    const ticket = requireSupportTicket(response?.ticket);
    window.dispatchEvent(new CustomEvent('plumme-support-tickets-changed', { detail: ticket }));
    return ticket;
  };

  // Customer-facing "delete". The backend soft-deletes (archives) the ticket, which is
  // idempotent. The event carries no ticket detail so list listeners refetch instead of
  // upserting the removed ticket back.
  const archiveSupportTicket = async (ticketId) => {
    const response = await supportRequest('archive', { ticketId });
    if (response?.archived !== true) throw new Error(window.PlummeI18n.t('err.nativeValidation.backend-support.1'));
    window.dispatchEvent(new CustomEvent('plumme-support-tickets-changed', { detail: { archivedTicketId: ticketId } }));
    return { ticketId: response.ticketId || ticketId };
  };

  // Count of tickets with an unread admin reply for the active business. Used
  // by the shell nav badge (`src/shell.jsx`) — polled on mount/focus/ticket
  // change rather than a live `onSnapshot`, since support tickets have no
  // Firestore rules match block and stay closed to client SDKs.
  const getSupportUnreadCount = async () => {
    const response = await supportRequest('unreadCount');
    return Number(response?.count) || 0;
  };

  // Published help articles (Firestore-backed, managed in Plumme Admin). Not tied to a
  // business, so it works before a business is selected. The cache and fallback logic live
  // in `src/services/helpArticleStore.js`; this is only the authenticated transport.
  const fetchHelpArticles = ({ locale, ifVersion } = {}) => authorizedPost('getHelpArticlesHttp', {
    ...(locale ? { locale } : {}),
    ...(ifVersion ? { ifVersion } : {}),
  });

  const getHelpArticlesUserId = () => String(runtime.user?.uid || runtime.auth?.currentUser?.uid || '');

  global.PlummeBackendSupport = {
    fetchHelpArticles,
    getHelpArticlesUserId,
    warmSupportService,
    listSupportTickets,
    getSupportTicketThread,
    createSupportTicket,
    replyToSupportTicket,
    setSupportTicketStatus,
    archiveSupportTicket,
    getSupportUnreadCount,
  };
})(typeof window !== 'undefined' ? window : globalThis);
